import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { ViewMode } from '../../hooks/viewMode';
import carColumns from './carColumns';
import profileColumns from './profileColumns';
import areaColumns from './areaColumns';
import careColumns from './careColumns';
import claimColumns from './claimColumns';
import styles from './ColumnPanel.styles';
import tripColumns from '../../components/ColumnSelector/tripColumns';
import bookingColumns from '../../components/ColumnSelector/bookingColumns';
import {
  selectDisplayColumnsForArea,
  selectDisplayColumnsForBooking,
  selectDisplayColumnsForCar,
  selectDisplayColumnsForCare,
  selectDisplayColumnsForCategory,
  selectDisplayColumnsForCircle,
  selectDisplayColumnsForClaim,
  selectDisplayColumnsForPricing,
  selectDisplayColumnsForPrivacyPolicy,
  selectDisplayColumnsForProfile,
  selectDisplayColumnsForTimeTable,
  selectDisplayColumnsForTrip,
  selectViewMode,
  setDisplayColumns,
} from '../../redux/store/ui/common';
import categoryColumns from '../../components/ColumnSelector/categoryColumns';
import circleColumns from '../../components/ColumnSelector/circleColumns';
import pricingColumns from '../../components/ColumnSelector/pricingColumns';
import {
  selectDisplayColumnsForAdmins,
  selectDisplayColumnsForFuelTransactions,
  selectDisplayColumnsForReceipts,
  selectDisplayColumnsForReports,
  selectDisplayColumnsForUsers,
  selectViewType,
} from '../../redux/store/ui/common/selectors';
import usersColumns from './usersColumns';
import { ViewType } from '../../hooks/useViewType';
import timeTableColumns from './timeTableColumns';
import { useIntl } from 'react-intl';
import adminsColumns from './adminsColumns';
import { valueToTextKey } from './columnsValueToTextKey';
import receiptsColumns from './receiptsColumns';
import fuelTransactionsColumns from './fuelTransactionsColumns';
import privacyPolicyColumns from './privacyPolicyColumns';
import reportsColumns from './reportsColumns';

const ColumnPanel: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const viewMode = useSelector(selectViewMode);
  const viewType = useSelector(selectViewType);
  const vehicleValues = useSelector(selectDisplayColumnsForCar);
  const timeTableValues = useSelector(selectDisplayColumnsForTimeTable);
  const areaValues = useSelector(selectDisplayColumnsForArea);
  const careValues = useSelector(selectDisplayColumnsForCare);
  const tripValues = useSelector(selectDisplayColumnsForTrip);
  const bookingValues = useSelector(selectDisplayColumnsForBooking);
  const claimValues = useSelector(selectDisplayColumnsForClaim);
  const categoryValues = useSelector(selectDisplayColumnsForCategory);
  const pricingsValues = useSelector(selectDisplayColumnsForPricing);
  const privacyPolicyValues = useSelector(selectDisplayColumnsForPrivacyPolicy);
  const circleValues = useSelector(selectDisplayColumnsForCircle);
  const profileValues = useSelector(selectDisplayColumnsForProfile);
  const receiptsValues = useSelector(selectDisplayColumnsForReceipts);
  const fuelTransactionsValues = useSelector(
    selectDisplayColumnsForFuelTransactions
  );
  const usersValues = useSelector(selectDisplayColumnsForUsers);
  const adminsValues = useSelector(selectDisplayColumnsForAdmins);
  const reportsValues = useSelector(selectDisplayColumnsForReports);

  const options: any =
    viewType === ViewType.TIME_TABLE
      ? timeTableColumns
      : viewMode === ViewMode.VEHICLE
      ? carColumns
      : viewMode === ViewMode.PROFILE
      ? profileColumns
      : viewMode === ViewMode.AREA
      ? areaColumns
      : viewMode === ViewMode.CARE
      ? careColumns
      : viewMode === ViewMode.SHARE
      ? tripColumns
      : viewMode === ViewMode.BOOKING
      ? bookingColumns
      : viewMode === ViewMode.CLAIM
      ? claimColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.claim.table.${valueToTextKey(column.value)}`,
          }),
        }))
      : viewMode === ViewMode.CATEGORIES
      ? categoryColumns
      : viewMode === ViewMode.PRICING
      ? pricingColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.pricing.${column.value.toLowerCase()}`,
          }),
        }))
      : viewMode === ViewMode.PRIVACY_POLICY
      ? privacyPolicyColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.policy.${column.value.toLowerCase()}`,
          }),
        }))
      : viewMode === ViewMode.RECEIPTS
      ? receiptsColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.receipts.table.${valueToTextKey(
              column.value
            ).toLowerCase()}`,
          }),
        }))
      : viewMode === ViewMode.FUEL_TRANSACTIONS
      ? fuelTransactionsColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.fuelTransactions.table.${column.value.toLowerCase()}`,
            defaultMessage: column.label,
          }),
        }))
      : viewMode === ViewMode.CIRCLE
      ? circleColumns
      : viewMode === ViewMode.USERS
      ? usersColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.user.table.${column.value.toLowerCase()}`,
          }),
        }))
      : viewMode === ViewMode.ADMINS
      ? adminsColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.admins.table.${column.value.toLowerCase()}`,
          }),
        }))
      : viewMode === ViewMode.REPORTS
      ? reportsColumns.map((column) => ({
          ...column,
          label: intl.formatMessage({
            id: `fleetadmin.reports.table.${column.value.toLowerCase()}`,
          }),
        }))
      : null;

  const localizedOptions: any = options?.map((column: any) =>
    column.textId
      ? {
          ...column,
          label: intl.formatMessage({
            id: column.textId,
            defaultMessage: column.label,
          }),
        }
      : column
  );

  const value: any =
    viewType === ViewType.TIME_TABLE
      ? timeTableValues
      : viewMode === ViewMode.VEHICLE
      ? vehicleValues
      : viewMode === ViewMode.PROFILE
      ? profileValues
      : viewMode === ViewMode.AREA
      ? areaValues
      : viewMode === ViewMode.CARE
      ? careValues
      : viewMode === ViewMode.SHARE
      ? tripValues
      : viewMode === ViewMode.BOOKING
      ? bookingValues
      : viewMode === ViewMode.CLAIM
      ? claimValues
      : viewMode === ViewMode.CATEGORIES
      ? categoryValues
      : viewMode === ViewMode.PRICING
      ? pricingsValues
      : viewMode === ViewMode.PRIVACY_POLICY
      ? privacyPolicyValues
      : viewMode === ViewMode.RECEIPTS
      ? receiptsValues
      : viewMode === ViewMode.FUEL_TRANSACTIONS
      ? fuelTransactionsValues
      : viewMode === ViewMode.CIRCLE
      ? circleValues
      : viewMode === ViewMode.USERS
      ? usersValues
      : viewMode === ViewMode.ADMINS
      ? adminsValues
      : viewMode === ViewMode.REPORTS
      ? reportsValues
      : null;

  const target =
    viewType === ViewType.TIME_TABLE
      ? 'timeTable'
      : viewMode === ViewMode.VEHICLE
      ? 'car'
      : viewMode === ViewMode.PROFILE
      ? 'profile'
      : viewMode === ViewMode.AREA
      ? 'area'
      : viewMode === ViewMode.CARE
      ? 'care'
      : viewMode === ViewMode.SHARE
      ? 'trip'
      : viewMode === ViewMode.BOOKING
      ? 'booking'
      : viewMode === ViewMode.CLAIM
      ? 'claim'
      : viewMode === ViewMode.CATEGORIES
      ? 'category'
      : viewMode === ViewMode.PRICING
      ? 'pricing'
      : viewMode === ViewMode.PRIVACY_POLICY
      ? 'privacyPolicy'
      : viewMode === ViewMode.RECEIPTS
      ? 'receipts'
      : viewMode === ViewMode.FUEL_TRANSACTIONS
      ? 'fuelTransactions'
      : viewMode === ViewMode.CIRCLE
      ? 'circle'
      : viewMode === ViewMode.USERS
      ? 'users'
      : viewMode === ViewMode.ADMINS
      ? 'admins'
      : viewMode === ViewMode.REPORTS
      ? 'reports'
      : null;

  const onChange = (values: any) => {
    // @ts-ignore
    dispatch(setDisplayColumns({ [target]: values }));
  };

  return (
    <>
      <div className="column-panel-wrapper">
        <Checkbox.Group
          options={localizedOptions}
          defaultValue={value}
          onChange={onChange}
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default ColumnPanel;
